import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest, ApiRequestError } from '../api/client.js';
import type { AvailableTransition, WorkOrderDetail } from '../api/types.js';
import { workOrderStatusLabel } from '../../shared/workflow.js';
import type { WorkOrderStatus } from '../../shared/workflow.js';
import { Modal } from '../components/Modal.js';
import { Alert, Button, Field, TextArea } from '../components/ui.js';

/**
 * The available transitions come from the API, which derives them from the same
 * table the database trigger enforces — so the buttons offered are exactly the
 * moves the caller is permitted to make.
 */
export function StatusActions({
  workOrder,
  availableTransitions,
}: {
  workOrder: WorkOrderDetail;
  availableTransitions: AvailableTransition[];
}) {
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<AvailableTransition | null>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const transition = useMutation({
    mutationFn: (payload: { toStatus: WorkOrderStatus; reason?: string }) =>
      apiRequest(`/api/work-orders/${workOrder.id}/transition`, {
        method: 'POST',
        body: payload,
      }),
    onSuccess: () => {
      setPending(null);
      setReason('');
      void queryClient.invalidateQueries({ queryKey: ['work-order', workOrder.id] });
      void queryClient.invalidateQueries({ queryKey: ['work-orders'] });
      void queryClient.invalidateQueries({ queryKey: ['timeline', workOrder.id] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: (caught: unknown) => {
      setError(
        caught instanceof ApiRequestError
          ? caught.message
          : 'The status could not be changed. Please try again.',
      );
    },
  });

  const visibleTransitions =
    workOrder.kind === 'supplies'
      ? availableTransitions.filter(
          (available) => !['repair_scheduled', 'work_in_progress'].includes(available.toStatus),
        )
      : availableTransitions;

  if (visibleTransitions.length === 0) return null;

  const label = (status: WorkOrderStatus) => workOrderStatusLabel(status, workOrder.kind);
  const reasonRequired = pending?.reasonRequired ?? false;

  return (
    <>
      {visibleTransitions.map((available) => (
        <Button
          key={available.toStatus}
          variant={
            available.toStatus === 'cancelled' || available.toStatus === 'rejected'
              ? 'danger'
              : 'secondary'
          }
          onClick={() => {
            setError(null);
            setReason('');
            if (available.reasonRequired || promptsForReason(available.toStatus)) {
              setPending(available);
            } else {
              transition.mutate({ toStatus: available.toStatus });
            }
          }}
          loading={
            transition.isPending &&
            !available.reasonRequired &&
            !promptsForReason(available.toStatus)
          }
        >
          {actionLabel(available.toStatus, workOrder.status, workOrder.kind)}
        </Button>
      ))}

      <Modal
        open={pending !== null}
        onClose={() => setPending(null)}
        title={pending ? `Move to ${label(pending.toStatus)}` : ''}
        description={
          reasonRequired
            ? 'A reason is required and becomes part of the permanent record.'
            : 'A reason is optional; anything you enter becomes part of the permanent record.'
        }
        footer={
          <>
            <Button variant="secondary" onClick={() => setPending(null)}>
              Cancel
            </Button>
            <Button
              variant={
                pending?.toStatus === 'cancelled' || pending?.toStatus === 'rejected'
                  ? 'danger'
                  : 'primary'
              }
              disabled={reasonRequired && reason.trim().length < 3}
              loading={transition.isPending}
              onClick={() => {
                if (!pending) return;
                setError(null);
                const trimmed = reason.trim();
                transition.mutate({
                  toStatus: pending.toStatus,
                  ...(trimmed === '' ? {} : { reason: trimmed }),
                });
              }}
            >
              Confirm
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          {error && <Alert variant="error">{error}</Alert>}
          <Field label="Reason" htmlFor="transition-reason" required={reasonRequired}>
            <TextArea
              id="transition-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              rows={4}
              autoFocus
            />
          </Field>
        </div>
      </Modal>
    </>
  );
}

/**
 * Closing is the end of the record, so the closer is always given the chance to say
 * why — but the reason is theirs to give, not a gate on the action.
 */
function promptsForReason(status: WorkOrderStatus): boolean {
  return status === 'closed';
}

/** Reads as an action rather than a state name where that is clearer. */
function actionLabel(
  status: WorkOrderStatus,
  fromStatus: WorkOrderStatus,
  kind?: 'repair' | 'supplies',
): string {
  // Releasing a hold has one destination, so it reads as resuming rather than as a move.
  if (fromStatus === 'on_hold' && status !== 'cancelled') {
    return `Resume (back to ${workOrderStatusLabel(status, kind)})`;
  }

  switch (status) {
    case 'under_vgs_review':
      return 'Acknowledge';
    case 'estimate_in_progress':
      return 'Begin estimate';
    case 'on_hold':
      return 'Place on hold';
    case 'cancelled':
      return 'Cancel work order';
    case 'work_in_progress':
      return 'Start work';
    case 'closed':
      return 'Close work order';
    default:
      return `Move to ${workOrderStatusLabel(status, kind)}`;
  }
}
